import { Order, SubscriptionType } from "../webapp/src/types";

export type GenevaIssueCode =
    | "missing_initial_subscription_journal_transaction"
    | "initial_subscription_journal_transaction_not_success"
    | "missing_reversal_subscription_journal_transaction"
    | "reversal_subscription_journal_transaction_not_success"
    | "missing_subscription_journal_transaction"
    | "subscription_journal_transaction_not_success"
    | "missing_redemption_journal_transaction"
    | "redemption_journal_transaction_not_success"
    | "missing_gwi_order"
    | "gwi_order_not_success"
    | "processing_order_has_zero_units"
    | "missing_transfer_date";

export interface GenevaValidationResult {
    hasAllRequiredFields: boolean;
    missingFields: GenevaIssueCode[];
}

const hasResponseField = (value?: Record<string, unknown> | null) => {
    if (!value) {
        return false;
    }
    if (typeof value !== "object") {
        return false;
    }
    return Object.keys(value).length > 0;
};

const hasSuccessfulResponse = (value?: Record<string, unknown> | null) => {
    if (!hasResponseField(value)) {
        return false;
    }
    const statusValue = (value as { status?: unknown }).status;
    if (typeof statusValue !== "string") {
        return false;
    }
    return statusValue.toLowerCase() === "success";
};

const hasGenevaResponses = (order?: Order) => order?.geneva_responses ?? {};

export const validateWaitingForNavSubscription = (order: Order): GenevaValidationResult => {
    if (order.type !== "subscription") {
        return { hasAllRequiredFields: true, missingFields: [] };
    }

    const response = hasGenevaResponses(order);
    const missingFields: GenevaIssueCode[] = [];

    if (!hasResponseField(response.initial_subscription_journal_transaction)) {
        missingFields.push("missing_initial_subscription_journal_transaction");
    } else if (!hasSuccessfulResponse(response.initial_subscription_journal_transaction)) {
        missingFields.push("initial_subscription_journal_transaction_not_success");
    }

    //check if transferDate exists
    if (!order.transferDate) {
        missingFields.push("missing_transfer_date");
    }

    return {
        hasAllRequiredFields: missingFields.length === 0,
        missingFields,
    };
};

const hasZeroUnits = (order: Order) => typeof order.units === "number" && order.units <= 0;

const validateSubscriptionProcessing = (order: Order): GenevaValidationResult => {
    const response = hasGenevaResponses(order);
    const missingFields: GenevaIssueCode[] = [];

    if (hasZeroUnits(order)) {
        missingFields.push("processing_order_has_zero_units");
    }
    if (!hasResponseField(response.reversal_subscription_journal_transaction)) {
        missingFields.push("missing_reversal_subscription_journal_transaction");
    } else if (!hasSuccessfulResponse(response.reversal_subscription_journal_transaction)) {
        missingFields.push("reversal_subscription_journal_transaction_not_success");
    }
    if (!hasResponseField(response.subscription_journal_transaction)) {
        missingFields.push("missing_subscription_journal_transaction");
    } else if (!hasSuccessfulResponse(response.subscription_journal_transaction)) {
        missingFields.push("subscription_journal_transaction_not_success");
    }
    if (!hasResponseField(response.gwi_order)) {
        missingFields.push("missing_gwi_order");
    } else if (!hasSuccessfulResponse(response.gwi_order)) {
        missingFields.push("gwi_order_not_success");
    }

    return {
        hasAllRequiredFields: missingFields.length === 0,
        missingFields,
    };
};

const validateRedemptionProcessing = (order: Order): GenevaValidationResult => {
    const response = hasGenevaResponses(order);
    const missingFields: GenevaIssueCode[] = [];

    if (hasZeroUnits(order)) {
        missingFields.push("processing_order_has_zero_units");
    }
    if (!hasResponseField(response.redemption_journal_transaction)) {
        missingFields.push("missing_redemption_journal_transaction");
    } else if (!hasSuccessfulResponse(response.redemption_journal_transaction)) {
        missingFields.push("redemption_journal_transaction_not_success");
    }
    if (!hasResponseField(response.gwi_order)) {
        missingFields.push("missing_gwi_order");
    } else if (!hasSuccessfulResponse(response.gwi_order)) {
        missingFields.push("gwi_order_not_success");
    }

    return {
        hasAllRequiredFields: missingFields.length === 0,
        missingFields,
    };
};

export const validateProcessingGenevaResponses = (order: Order): GenevaValidationResult => {
    const validators: Record<SubscriptionType, (order: Order) => GenevaValidationResult> = {
        subscription: validateSubscriptionProcessing,
        redemption: validateRedemptionProcessing,
    };

    const validator = validators[order.type];
    return validator ? validator(order) : { hasAllRequiredFields: true, missingFields: [] };
};

const issueDescriptions: Record<GenevaIssueCode, string> = {
    missing_initial_subscription_journal_transaction: "Missing initial subscription journal transaction",
    initial_subscription_journal_transaction_not_success: "Initial subscription journal transaction not successful",
    missing_reversal_subscription_journal_transaction: "Missing reversal subscription journal transaction",
    reversal_subscription_journal_transaction_not_success: "Reversal subscription journal transaction not successful",
    missing_subscription_journal_transaction: "Missing subscription journal transaction",
    subscription_journal_transaction_not_success: "Subscription journal transaction not successful",
    missing_redemption_journal_transaction: "Missing redemption journal transaction",
    redemption_journal_transaction_not_success: "Redemption journal transaction not successful",
    missing_gwi_order: "Missing GWI order payload",
    gwi_order_not_success: "GWI order has not completed successfully",
    processing_order_has_zero_units: "Processing order has zero units",
    missing_transfer_date: "Missing transfer date for subscription",
};

export const describeGenevaIssue = (issue: GenevaIssueCode): string => issueDescriptions[issue] ?? issue;
